import { useEffect, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { FileNameDisplay } from '@proton/components/index';
import { MemberRole, type NodeEntity } from '@proton/drive';
import { getNodeName } from '@proton/drive/modules/nodes';

import { ContentPreviewMethod, PartialPreview } from '../../modals/preview';
import { downloadManager } from '../../modules/download/DownloadManager';
import { getOpenInDocsInfo } from '../../utils/docs/openInDocs';
import { PublicHeader } from './PublicHeader';
import { usePublicActions } from './actions/usePublicActions';
import { getPublicLinkClient } from './publicLinkClient';
import { usePublicAuthStore } from './usePublicAuth.store';

interface PublicFileViewProps {
    rootNode: NodeEntity;
    customPassword?: string;
    isPartialView?: boolean;
}

export const PublicFileView = ({ rootNode, customPassword, isPartialView }: PublicFileViewProps) => {
    const [contentData, setContentData] = useState<Uint8Array<ArrayBuffer>[] | undefined>(undefined);
    const { modals, handleDetails, handleOpenDocsOrSheets, handleCopyLink } = usePublicActions();
    const { isLoggedIn, isEditor } = usePublicAuthStore(
        useShallow((state) => ({ isEditor: state.publicRole === MemberRole.Editor, isLoggedIn: state.isLoggedIn }))
    );
    const canVerifySignature = isLoggedIn && isEditor;
    const canSeeEmail = isEditor;
    useEffect(() => {
        const openInDocsInfo = rootNode.mediaType ? getOpenInDocsInfo(rootNode.mediaType) : undefined;

        // Conversion cannot be done for single public file as we can't write on parent folder
        if (openInDocsInfo?.isNative) {
            handleOpenDocsOrSheets(rootNode.uid, openInDocsInfo, customPassword);
        }
    }, [rootNode.uid, rootNode.mediaType, rootNode.parentUid, handleOpenDocsOrSheets, customPassword]);

    const handleContentLoaded = (data: Uint8Array<ArrayBuffer>[], previewMethod?: ContentPreviewMethod) => {
        if (previewMethod === ContentPreviewMethod.Buffer) {
            setContentData(data);
        }
    };
    const handleDownload = async (shouldScan?: boolean) => {
        if (contentData && !shouldScan) {
            const publicNode = await getPublicLinkClient().getNode(rootNode.uid);
            await downloadManager.downloadFromBuffer(publicNode, contentData);
            return;
        }

        if (shouldScan) {
            await downloadManager.downloadAndScan([rootNode.uid], { skipSignatureCheck: true });
        } else {
            await downloadManager.download([rootNode.uid], { skipSignatureCheck: true });
        }
    };

    return (
        <div className="h-full flex flex-column">
            <PublicHeader
                breadcrumbOrName={
                    <div className="file-preview-filename flex items-center flex-nowrap">
                        <FileNameDisplay text={getNodeName(rootNode)} className="user-select w-100" />
                    </div>
                }
                sharedBy={
                    canSeeEmail
                        ? (rootNode.keyAuthor.ok ? rootNode.keyAuthor.value : rootNode.keyAuthor.error.claimedAuthor) ||
                          undefined
                        : undefined
                }
                onDetails={canVerifySignature ? () => handleDetails(rootNode.uid) : undefined}
                onDownload={handleDownload}
                onScanAndDownload={() => handleDownload(true)}
                onCopyLink={handleCopyLink}
                customPassword={customPassword}
                isPartialView={isPartialView}
            />
            <PartialPreview
                className="flex-1"
                drive={getPublicLinkClient()}
                nodeUid={rootNode.uid}
                verifySignatures={canVerifySignature}
                // We cannot convert a doc/sheets for single file view
                canOpenInDocs={false}
                onContentLoaded={handleContentLoaded}
                onDownload={() => handleDownload()}
            />
            {modals.detailsModal}
        </div>
    );
};
